import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../LanguageContext';
import { useBrand } from '../BrandContext';
import { sortProjects } from '../components/UnitsDeals/constants';
import { projectMediaList, projectMediaUpload, projectMediaDelete, projectMediaBlobUrl, unitsProjectsList } from '../api';

const fmtSize = (n) => (n > 1048576 ? (n / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(n / 1024)) + ' KB');

// One card = one media file. The blob is fetched with the auth header (never a
// token in the URL); images load a thumbnail eagerly, videos load on play.
function MediaCard({ item, canManage, onDelete, t, ar }) {
  const isVideo = (item.mime || '').startsWith('video/');
  const isPdf = (item.mime || '') === 'application/pdf' || item.type === 'brochure';
  const typeName = (item.type === 'brochure' || isPdf) ? t('بروشور', 'Brochure') : (item.type === 'video' || isVideo) ? t('فيديو', 'Video') : t('صورة', 'Image');
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);

  const cardRef = useRef(null);

  const load = useCallback(async () => {
    if (url || loading) return url;
    setLoading(true);
    try { const u = await projectMediaBlobUrl(item.id); setUrl(u); setLoading(false); return u; }
    catch { setErr(true); setLoading(false); return null; }
  }, [item.id, url, loading]);

  // Lazy-load image thumbnails only when the card scrolls into view — loading EVERY
  // image's full blob on mount was the main cause of the slow الوسائط page (a project
  // with dozens of images fired dozens of full-size downloads at once). Videos/PDFs
  // still load only on explicit play/download.
  useEffect(() => {
    if (isVideo || isPdf || url) return;
    const el = cardRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') { load(); return; }
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { load(); obs.disconnect(); }
    }, { rootMargin: '200px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [isVideo, isPdf, url, load]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const download = async () => {
    const u = url || await load();
    if (!u) return;
    const a = document.createElement('a');
    a.href = u; a.download = item.title || (isPdf ? 'brochure.pdf' : isVideo ? 'video' : 'image');
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div ref={cardRef} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', width: 220 }}>
      <div style={{ height: 140, background: 'var(--bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {err ? (
          <span style={{ color: 'var(--danger, #dc2626)', fontSize: '0.8rem' }}>{t('تعذّر التحميل', 'Failed to load')}</span>
        ) : isVideo ? (
          url ? (
            <video src={url} controls autoPlay style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }} />
          ) : (
            <button onClick={load} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 700 }}>
              {loading ? '…' : `▶ ${t('تشغيل الفيديو', 'Play video')}`}
            </button>
          )
        ) : isPdf ? (
          <div onClick={download} title={t('فتح / تنزيل', 'Open / download')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <span style={{ fontSize: '2.8rem' }}>📄</span>
            <span style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', fontWeight: 700 }}>{t('بروشور PDF', 'PDF brochure')}</span>
          </div>
        ) : url ? (
          <img src={url} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>…</span>
        )}
      </div>
      <div style={{ padding: 10 }}>
        <div style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.title || typeName}</div>
        <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: 8 }}>{typeName} · {fmtSize(item.size)}</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={download} style={{ flex: 1, padding: '6px 8px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
            {ar ? 'تنزيل' : 'Download'}
          </button>
          {canManage && (
            <button onClick={() => onDelete(item)} style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid rgba(239,68,68,0.45)', background: 'none', color: '#ef4444', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
              {ar ? 'حذف' : 'Delete'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ProjectMedia({ canManage = false }) {
  const { lang, isRTL, t: tr } = useLanguage();
  const ar = lang === 'ar';
  const t = (a, e) => (ar ? a : e);
  const brand = useBrand();
  // Prefer the app's project label (translation key wizProject_<key>, EN+AR), then
  // the tenant's configured projectLabels, then the raw project key.
  const plabel = (p) => {
    const k = 'wizProject_' + p;
    const v = tr(k);
    if (v && v !== k) return v;
    const l = brand.projectLabels?.[p];
    return l ? (ar ? (l.ar || l.en) : (l.en || l.ar)) : p;
  };
  const [media, setMedia] = useState([]);
  const [unitProjects, setUnitProjects] = useState([]);
  // Union of: real inventory projects (with units), the configured PROJECTS, and
  // any project that already has media — deduped, inventory projects first. So the
  // picker covers both projects that have units and brand-new/empty ones.
  const projects = sortProjects(Array.from(new Set([
    ...unitProjects,
    ...(brand.projects || []),
    ...media.map((m) => m.project),
  ])).filter(Boolean));
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [typeTab, setTypeTab] = useState('all'); // all | image | video | brochure
  // upload form
  const [upProject, setUpProject] = useState('');
  const [upTitle, setUpTitle] = useState('');
  const [upFile, setUpFile] = useState(null);
  const [upType, setUpType] = useState('image'); // image | video | brochure (user-chosen)
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await projectMediaList();
    setMedia(res.media || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canManage) unitsProjectsList().then((r) => setUnitProjects(r.projects || [])); }, [canManage]);

  const errText = (code) => ({
    project_required: t('اختر المشروع', 'Pick a project'),
    only_media: t('يُسمح بالصور والفيديو وملفات PDF فقط', 'Only images, videos, or PDF files are allowed'),
    file_too_large: t('الملف كبير جداً (الحد 48 ميجابايت)', 'File too large (max 48 MB)'),
    empty_file: t('اختر ملفاً', 'Choose a file'),
  }[code] || t('تعذّر الرفع، حاول مجدداً', 'Upload failed, please retry'));

  const upload = async () => {
    setMsg(null);
    if (!upProject) { setMsg({ type: 'error', text: errText('project_required') }); return; }
    if (!upFile) { setMsg({ type: 'error', text: errText('empty_file') }); return; }
    if (upFile.size > 48 * 1024 * 1024) { setMsg({ type: 'error', text: errText('file_too_large') }); return; }
    setBusy(true);
    const res = await projectMediaUpload(upFile, upProject, upTitle || upFile.name, upType);
    setBusy(false);
    if (res.error) { setMsg({ type: 'error', text: errText(res.error) }); return; }
    setMsg({ type: 'success', text: t('تم الرفع', 'Uploaded') });
    setUpTitle(''); setUpFile(null);
    const fileInput = document.getElementById('pm-file'); if (fileInput) fileInput.value = '';
    load();
  };

  const remove = async (item) => {
    if (!window.confirm(t(`حذف "${item.title || 'الملف'}"؟`, `Delete "${item.title || 'file'}"?`))) return;
    const res = await projectMediaDelete(item.id);
    if (res.error) { window.alert(t('تعذّر الحذف', 'Delete failed')); return; }
    load();
  };

  // Classify each item so brochures / images / videos can be shown separately.
  const mediaType = (m) => (m.type === 'brochure' || (m.mime || '') === 'application/pdf') ? 'brochure'
    : (m.type === 'video' || (m.mime || '').startsWith('video/')) ? 'video' : 'image';
  const inProject = filter ? media.filter((m) => m.project === filter) : media;
  const typeCounts = { all: inProject.length, image: 0, video: 0, brochure: 0 };
  inProject.forEach((m) => { typeCounts[mediaType(m)] += 1; });
  const shown = typeTab === 'all' ? inProject : inProject.filter((m) => mediaType(m) === typeTab);
  const TYPE_TABS = [['all', t('الكل', 'All')], ['image', t('الصور', 'Images')], ['video', t('الفيديوهات', 'Videos')], ['brochure', t('البروشور', 'Brochures')]];
  // Folder view: per-project counts computed from metadata only (no files loaded).
  const folderMap = {};
  media.forEach((m) => {
    const f = folderMap[m.project] || (folderMap[m.project] = { project: m.project, count: 0, image: 0, video: 0, brochure: 0 });
    f.count += 1; f[mediaType(m)] += 1;
  });
  const folders = sortProjects(Object.keys(folderMap)).map((p) => folderMap[p]);
  const input = { padding: '9px 11px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.88rem' };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ maxWidth: 1040, margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: 14 }}>{t('وسائط المشاريع', 'Project media')}</h1>

      {canManage && (
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ fontWeight: 800, marginBottom: 10, color: 'var(--text-primary)' }}>{t('رفع وسائط (صورة / فيديو / بروشور)', 'Upload media (image / video / brochure)')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 10 }}>
            <select style={input} value={upProject} onChange={(e) => setUpProject(e.target.value)}>
              <option value="">{t('— اختر المشروع —', '— Select project —')}</option>
              {projects.map((p) => <option key={p} value={p}>{plabel(p)}</option>)}
            </select>
            <select style={input} value={upType} onChange={(e) => setUpType(e.target.value)}>
              <option value="image">{t('صورة', 'Image')}</option>
              <option value="video">{t('فيديو', 'Video')}</option>
              <option value="brochure">{t('بروشور (PDF)', 'Brochure (PDF)')}</option>
            </select>
            <input style={input} placeholder={t('عنوان (اختياري)', 'Title (optional)')} value={upTitle} onChange={(e) => setUpTitle(e.target.value)} />
            <input id="pm-file" style={input} type="file" accept="image/*,video/*,application/pdf" onChange={(e) => {
              const f = e.target.files?.[0] || null; setUpFile(f);
              // Suggest the category from the file; the user can still change it.
              if (f) setUpType(f.type === 'application/pdf' ? 'brochure' : (f.type || '').startsWith('video/') ? 'video' : 'image');
            }} />
          </div>
          {msg && <div style={{ marginBottom: 10, fontSize: '0.84rem', fontWeight: 700, color: msg.type === 'success' ? '#16a34a' : '#ef4444' }}>{msg.text}</div>}
          <button onClick={upload} disabled={busy} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 800, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? t('جارٍ الرفع…', 'Uploading…') : t('رفع', 'Upload')}
          </button>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 8 }}>{t('الحد الأقصى 48 ميجابايت للملف. للفيديوهات الكبيرة استخدم مقاطع قصيرة.', 'Max 48 MB per file. For big videos use short clips.')}</div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>…</div>
      ) : !filter ? (
        /* ── Folders: one card per project (NO media loaded until you open one) ── */
        folders.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>{t('لا توجد وسائط بعد', 'No media yet')}</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 14 }}>
            {folders.map((f) => {
              const chip = (icon, n) => n > 0 ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999, background: 'var(--bg-primary)', border: '1px solid var(--border)', fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)' }}>{icon} {n}</span>
              ) : null;
              return (
                <button key={f.project} onClick={() => { setFilter(f.project); setTypeTab('all'); }}
                  style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'stretch', textAlign: isRTL ? 'right' : 'left', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 14, padding: 18, cursor: 'pointer', minHeight: 128 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span style={{ fontSize: '1.7rem', lineHeight: 1, flexShrink: 0 }}>📁</span>
                    <span style={{ fontWeight: 800, fontSize: '1.02rem', color: 'var(--text-primary)', lineHeight: 1.45, wordBreak: 'break-word', flex: 1, minWidth: 0 }}>{plabel(f.project)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {chip('🖼', f.image)}{chip('🎬', f.video)}{chip('📄', f.brochure)}
                  </div>
                  <div style={{ fontSize: '0.74rem', color: 'var(--accent)', fontWeight: 700, marginTop: 'auto' }}>
                    {f.count} {t('ملف', 'files')} — {t('افتح', 'Open')} {isRTL ? '←' : '→'}
                  </div>
                </button>
              );
            })}
          </div>
        )
      ) : (
        /* ── Inside a project: only THIS project's media loads (with type tabs) ── */
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <button onClick={() => setFilter('')} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem' }}>
              {isRTL ? '→ كل المشاريع' : '← All projects'}
            </button>
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800 }}>{plabel(filter)}</h2>
          </div>
          {/* Type tabs — separate البروشور / الصور / الفيديوهات from each other. */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            {TYPE_TABS.map(([key, lbl]) => (
              <button key={key} onClick={() => setTypeTab(key)}
                style={{ padding: '7px 14px', borderRadius: 999, border: '1px solid var(--border)', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700,
                  background: typeTab === key ? 'var(--accent)' : 'var(--bg-secondary)', color: typeTab === key ? '#fff' : 'var(--text-primary)' }}>
                {lbl} <span style={{ opacity: 0.7 }}>({typeCounts[key] || 0})</span>
              </button>
            ))}
          </div>
          {shown.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>{t('لا توجد وسائط بهذا التصنيف', 'No media in this category')}</div>
          ) : (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {shown.map((m) => <MediaCard key={m.id} item={m} canManage={canManage} onDelete={remove} t={t} ar={ar} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
